import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, RefreshCw, Calendar } from 'lucide-react';
import { Card } from '../components/ui/card';
import BottomNav from '../components/BottomNav';

const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  white: '#ffffff',
  gray: {
    100: '#f5f7f8',
    200: '#e8ecee',
    400: '#9ba4ab',
    500: '#6b7280',
    600: '#4c5b62'
  },
  success: '#10b981',
  error: '#ef4444'
};

const mockActivities = [
  {
    id: 1,
    type: 'deposit',
    description: 'Deposit to Financial Freedom',
    amount: 5000,
    date: '2025-01-15T10:30:00',
    status: 'completed',
    goalName: 'Financial Freedom'
  },
  {
    id: 2,
    type: 'performance',
    description: 'Velocity Portfolio Return',
    amount: 2450,
    date: '2025-01-14T16:00:00',
    status: 'completed',
    goalName: 'Financial Freedom',
    percentage: '+3.2%'
  },
  {
    id: 3,
    type: 'rebalance',
    description: 'Portfolio Rebalancing',
    amount: 0,
    date: '2025-01-13T09:00:00',
    status: 'completed',
    goalName: 'Financial Freedom'
  },
  {
    id: 4,
    type: 'deposit',
    description: 'Deposit to Beach House',
    amount: 3500,
    date: '2025-01-12T14:20:00',
    status: 'completed',
    goalName: 'Beach House'
  },
  {
    id: 5,
    type: 'withdrawal',
    description: 'Withdrawal from Children\'s Education',
    amount: 10000,
    date: '2025-01-10T11:45:00',
    status: 'completed',
    goalName: "Children's Education"
  },
  {
    id: 6,
    type: 'deposit',
    description: 'Monthly Contribution - Children\'s Education',
    amount: 8000,
    date: '2025-01-05T08:00:00',
    status: 'completed',
    goalName: "Children's Education"
  },
  {
    id: 7,
    type: 'performance',
    description: 'Horizon Portfolio Return',
    amount: 1820,
    date: '2025-01-04T16:00:00',
    status: 'completed',
    goalName: 'Beach House',
    percentage: '+4.1%'
  },
  {
    id: 8,
    type: 'rebalance',
    description: 'Quarterly Rebalancing',
    amount: 0,
    date: '2025-01-01T09:00:00',
    status: 'completed',
    goalName: 'All Portfolios'
  }
];

const Activity = () => {
  const [filter, setFilter] = useState('all');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-AU', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getIcon = (type) => {
    const iconProps = {
      size: 24,
      strokeWidth: 2
    };
    
    switch (type) {
      case 'deposit':
        return <ArrowDownCircle {...iconProps} color={COLORS.success} />;
      case 'withdrawal':
        return <ArrowUpCircle {...iconProps} color={COLORS.error} />;
      case 'performance':
        return <TrendingUp {...iconProps} color={COLORS.success} />;
      case 'rebalance':
        return <RefreshCw {...iconProps} color={COLORS.navy} />;
      default:
        return <Calendar {...iconProps} color={COLORS.gray[400]} />;
    }
  };

  const filteredActivities = filter === 'all' 
    ? mockActivities 
    : mockActivities.filter(a => a.type === filter);

  return (
    <div style={{ 
      backgroundColor: COLORS.beige, 
      minHeight: '100vh',
      paddingTop: '80px',
      paddingBottom: '100px'
    }}>
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-light mb-4" style={{ 
            fontFamily: 'Georgia, serif', 
            color: COLORS.navy 
          }}>
            Activity
          </h1>
          <p className="text-gray-600 text-lg">
            Track all your transactions and portfolio updates
          </p>
        </div>

        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          {[
            { value: 'all', label: 'All Activity' },
            { value: 'deposit', label: 'Deposits' },
            { value: 'withdrawal', label: 'Withdrawals' },
            { value: 'performance', label: 'Returns' },
            { value: 'rebalance', label: 'Rebalancing' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                background: filter === tab.value ? COLORS.orange : COLORS.white,
                color: filter === tab.value ? COLORS.white : COLORS.gray[600],
                transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Activity List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredActivities.map((activity) => (
            <Card key={activity.id} style={{
              padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transition: 'all 0.2s'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* Icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: COLORS.gray[100],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {getIcon(activity.type)}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: COLORS.navy,
                    marginBottom: '0.25rem'
                  }}>
                    {activity.description}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>
                      {activity.goalName}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: COLORS.gray[400] }}>
                      •
                    </span>
                    <span style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>
                      {formatDate(activity.date)}
                    </span>
                  </div>
                </div>

                {/* Amount */}
                {activity.amount !== 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: activity.type === 'withdrawal' ? COLORS.error : COLORS.success
                    }}>
                      {activity.type === 'withdrawal' ? '-' : '+'}{formatCurrency(activity.amount)}
                    </div>
                    {activity.percentage && (
                      <div style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>
                        {activity.percentage}
                      </div>
                    )}
                  </div>
                )}

                {activity.amount === 0 && (
                  <div style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '9999px',
                    background: COLORS.navy,
                    color: COLORS.white,
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    Completed
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {filteredActivities.length === 0 && (
          <Card style={{ padding: '3rem', textAlign: 'center' }}>
            <p style={{ fontSize: '1.125rem', color: COLORS.gray[500] }}>
              No {filter !== 'all' ? filter : ''} activity found
            </p>
          </Card>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Activity;